import { useCallback, useState } from 'react';
import { PageStatus } from '@/types/store';
import { useHideFunnelPage, useUpdateFunnelPage } from '../queries/funnel-page-mutations';
import { DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';
import type { FunnelPageItem } from '../types';

interface UseFunnelHidePageArgs {
    storeId: number | undefined;
    savedFunnelPages: FunnelPageItem[];
}

export interface FunnelHideEntryConfirmation {
    pageToHide: FunnelPageItem;
    newEntryPage: FunnelPageItem;
}

/**
 * Hide a funnel page. If hiding the entry page would promote the next visible
 * page (which still has upsell/downsell branches), surface a confirmation so
 * the user can detach those branches before hiding — entry pages can't branch.
 */
export function useFunnelHidePage({ storeId, savedFunnelPages }: UseFunnelHidePageArgs) {
    const hideFunnelPage = useHideFunnelPage(storeId ?? 0);
    const updateFunnelPage = useUpdateFunnelPage(storeId ?? 0);
    const [pendingEntryHide, setPendingEntryHide] = useState<FunnelHideEntryConfirmation | null>(null);

    const sortedSaved = [...savedFunnelPages]
        .filter(p => p.page_id !== DRAFT_FUNNEL_PAGE_ID)
        .sort((a, b) => a.order - b.order);
    const currentEntry = sortedSaved.find(p => p.status !== PageStatus.DRAFT) ?? null;

    const findNextEntryAfter = useCallback(
        (pageId: number): FunnelPageItem | null => {
            const idx = sortedSaved.findIndex(p => p.page_id === pageId);
            if (idx === -1) return null;
            for (let i = idx + 1; i < sortedSaved.length; i++) {
                const candidate = sortedSaved[i];
                if (candidate && candidate.status !== PageStatus.DRAFT) return candidate;
            }
            return null;
        },
        [sortedSaved]
    );

    const performHide = useCallback(
        (pageId: number) => {
            if (!storeId) return;
            hideFunnelPage.mutate(pageId);
        },
        [storeId, hideFunnelPage]
    );

    const requestHide = useCallback(
        (pageId: number) => {
            if (!storeId) return;
            if (pageId === DRAFT_FUNNEL_PAGE_ID) return;

            if (currentEntry?.page_id !== pageId) {
                performHide(pageId);
                return;
            }

            const newEntry = findNextEntryAfter(pageId);
            if (!newEntry || !newEntry.routing) {
                performHide(pageId);
                return;
            }

            const pageToHide = sortedSaved.find(p => p.page_id === pageId);
            if (!pageToHide) return;

            setPendingEntryHide({ pageToHide, newEntryPage: newEntry });
        },
        [storeId, currentEntry, findNextEntryAfter, performHide, sortedSaved]
    );

    const confirmEntryHide = useCallback(() => {
        if (!pendingEntryHide || !storeId) return;
        const { pageToHide, newEntryPage } = pendingEntryHide;
        if (!newEntryPage.referencePageId) {
            performHide(pageToHide.page_id);
            setPendingEntryHide(null);
            return;
        }

        updateFunnelPage.mutate(
            {
                page_id: newEntryPage.page_id,
                referencePageId: newEntryPage.referencePageId,
                productTitle: newEntryPage.title,
                order: newEntryPage.order,
                publish: newEntryPage.status === PageStatus.PUBLISHED,
                topBannerText: newEntryPage.topBannerText,
                funnelPrice: newEntryPage.funnelPrice,
                funnelCtaText: newEntryPage.funnelCtaText,
                routing: null,
            },
            {
                onSuccess: () => {
                    performHide(pageToHide.page_id);
                    setPendingEntryHide(null);
                },
                onError: () => setPendingEntryHide(null),
            }
        );
    }, [pendingEntryHide, storeId, updateFunnelPage, performHide]);

    const cancelEntryHide = useCallback(() => setPendingEntryHide(null), []);

    return {
        requestHide,
        pendingEntryHide,
        confirmEntryHide,
        cancelEntryHide,
        isHiding: hideFunnelPage.isPending || updateFunnelPage.isPending,
    };
}
